import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { deepCloneArray } from '../../utils/array';
import { getId } from '../../utils/item';
import { update } from '../../services/item';
import { isReadOnly } from '../../utils/roles';
import { DiceSettings } from './dice.Settings';
import { DiceDisplayHistory } from './dice.display.History';
import { DiceDisplayLast } from './dice.display.Last';
import {
  evaluateResultRule,
  resolveBound,
  resolveIntBound,
} from './dice.logic';

const DICE_SIDES = [4, 6, 8, 10, 12, 20, 100];
const ROLL_ANIMATION_MS = 2000;
const ROLL_TICK_MS = 60;

export const DiceGlobal = props => {
  const { itemId, diceId, dice, canEdit, fieldInGrid } = props;

  const { id: currentPageItemId } = useParams();
  const item = useSelector(state => state.items?.[itemId]);
  const items = useSelector(state => state.items);

  const canRoll = Boolean(canEdit) && isReadOnly() !== true;

  const historyLimit = Number.isFinite(dice?.historyLimit)
    ? Math.max(0, Math.min(50, dice.historyLimit))
    : 10;
  const history = Array.isArray(dice?.history) ? dice.history : [];
  const contextItem =
    currentPageItemId && items?.[currentPageItemId]
      ? items[currentPageItemId]
      : item;

  const buttons =
    Array.isArray(dice?.buttons) && dice.buttons.length > 0
      ? dice.buttons
      : DICE_SIDES.map(sides => ({
          id: null,
          label: `d${sides}`,
          count: 1,
          min: '1',
          max: String(sides),
          faces: [],
        }));

  const displayTypeRaw = fieldInGrid?.displayType || 'historique';
  const displayType =
    displayTypeRaw === 'simple' ? 'historique' : displayTypeRaw;
  const showLastOnly = displayType === 'dernier resultat';
  const gridW = Number(fieldInGrid?.w) || 0;
  const gridH = Number(fieldInGrid?.h) || 0;
  const isWideLayout = gridW > gridH;

  const lastSeenRollKeyRef = React.useRef(null);
  const mountedAtRef = React.useRef(Date.now());
  const [animByRollKey, setAnimByRollKey] = React.useState({});
  const animIntervalRef = React.useRef(null);
  const historyScrollRef = React.useRef(null);

  const getRollKey = entry => entry?.rollId || entry?.at || null;
  const newestKey = getRollKey(history?.[0]);

  React.useEffect(() => {
    return () => {
      if (animIntervalRef.current) {
        clearInterval(animIntervalRef.current);
        animIntervalRef.current = null;
      }
    };
  }, []);

  // Trigger a 2s animation for everyone when a new roll is received via store/websocket.
  React.useEffect(() => {
    const newest = history?.[0];
    const newestKey = getRollKey(newest);
    if (!newestKey) {
      return;
    }

    if (lastSeenRollKeyRef.current === null) {
      // First render: don't animate existing history.
      lastSeenRollKeyRef.current = newestKey;
      return;
    }

    if (newestKey === lastSeenRollKeyRef.current) {
      return;
    }

    lastSeenRollKeyRef.current = newestKey;
    setAnimByRollKey(prev => {
      if (prev?.[newestKey]) {
        return prev;
      }
      const count = Number.isFinite(newest?.count) ? newest.count : 1;
      const multiDisplay = newest?.multiDisplay === 'each' ? 'each' : 'total';
      const perMin = Number.isFinite(newest?.min)
        ? newest.min
        : Number.isFinite(newest?.sides)
          ? 1
          : 1;
      const perMax = Number.isFinite(newest?.max)
        ? newest.max
        : Number.isFinite(newest?.sides)
          ? newest.sides
          : 6;
      const totalMin = perMin * count;
      const totalMax = perMax * count;
      const low = Math.min(totalMin, totalMax);
      const high = Math.max(totalMin, totalMax);
      return {
        ...prev,
        [newestKey]: {
          perMin,
          perMax,
          min: low,
          max: high,
          buttonId: newest?.buttonId || null,
          buttonLabel:
            newest?.buttonLabel ||
            (Number.isFinite(newest?.sides) ? `d${newest.sides}` : null),
          count,
          multiDisplay,
          fromItemId: newest?.fromItemId,
          fromItemLabel: newest?.fromItemLabel,
          displayValue:
            low === high
              ? Math.round(low)
              : Math.floor(Math.random() * (high - low + 1)) + low,
          displayRolls: null,
          endAt: Date.now() + ROLL_ANIMATION_MS,
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newestKey]);

  // Chat-like behavior: always keep the latest roll visible (bottom).
  React.useEffect(() => {
    if (showLastOnly) {
      return;
    }
    const el = historyScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [newestKey, historyLimit, showLastOnly]);

  // Single global timer to animate all active rolls.
  React.useEffect(() => {
    const keys = Object.keys(animByRollKey || {});
    if (keys.length === 0) {
      if (animIntervalRef.current) {
        clearInterval(animIntervalRef.current);
        animIntervalRef.current = null;
      }
      return;
    }

    if (animIntervalRef.current) {
      return;
    }

    animIntervalRef.current = setInterval(() => {
      const now = Date.now();
      setAnimByRollKey(prev => {
        if (!prev) return prev;
        const next = {};
        let changed = false;
        for (const [k, anim] of Object.entries(prev)) {
          if (!anim || now >= anim.endAt) {
            changed = true;
            continue;
          }
          const count =
            Number.isFinite(anim?.count) && anim.count > 0 ? anim.count : 1;
          const multiDisplay = anim?.multiDisplay === 'each' ? 'each' : 'total';
          const perMin = Number.isFinite(anim?.perMin) ? anim.perMin : 1;
          const perMax = Number.isFinite(anim?.perMax) ? anim.perMax : 6;
          const perLow = Math.min(perMin, perMax);
          const perHigh = Math.max(perMin, perMax);
          const totalLow = perLow * count;
          const totalHigh = perHigh * count;

          let displayValue = anim.displayValue;
          let displayRolls = anim.displayRolls || null;
          if (multiDisplay === 'each' && count > 1) {
            displayRolls = Array.from({ length: count }).map(() =>
              perLow === perHigh
                ? Math.round(perLow)
                : Math.floor(Math.random() * (perHigh - perLow + 1)) + perLow,
            );
            displayValue = displayRolls.reduce((acc, n) => acc + (n || 0), 0);
          } else {
            displayValue =
              totalLow === totalHigh
                ? Math.round(totalLow)
                : Math.floor(Math.random() * (totalHigh - totalLow + 1)) +
                  totalLow;
            displayRolls = null;
          }

          next[k] = {
            ...anim,
            perMin,
            perMax,
            displayValue,
            displayRolls,
          };
          if (next[k].displayValue !== anim.displayValue) {
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }, ROLL_TICK_MS);

    return () => {
      if (animIntervalRef.current) {
        clearInterval(animIntervalRef.current);
        animIntervalRef.current = null;
      }
    };
  }, [animByRollKey]);

  const rollNow = button => {
    if (!canRoll) return;
    const minRaw = resolveBound(button?.min, contextItem, items);
    const maxRaw = resolveBound(button?.max, contextItem, items);
    const countRaw = resolveIntBound(button?.count ?? 1, contextItem, items);
    const count = Number.isFinite(countRaw)
      ? Math.max(1, Math.min(50, countRaw))
      : 1;
    if (!Number.isFinite(minRaw) || !Number.isFinite(maxRaw)) {
      return;
    }
    const low = Math.min(minRaw, maxRaw);
    const high = Math.max(minRaw, maxRaw);
    const rolls = [];
    for (let i = 0; i < count; i++) {
      const r =
        low === high
          ? Math.round(low)
          : Math.floor(Math.random() * (high - low + 1)) + low;
      rolls.push(r);
    }
    const value = rolls.reduce(
      (acc, n) => acc + (Number.isFinite(n) ? n : 0),
      0,
    );
    const fromItemId = currentPageItemId || null;
    const fromItemLabel = fromItemId
      ? items?.[fromItemId]?.label || null
      : null;
    const sourceItem =
      (fromItemId && items?.[fromItemId]?._id ? items[fromItemId] : null) ||
      contextItem ||
      item;
    const resultRule = evaluateResultRule(button, value, sourceItem, items);

    const entry = {
      rollId:
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : String(Date.now()) + '-' + String(Math.random()).slice(2),
      buttonId: button?.id || null,
      buttonLabel: button?.label || null,
      count,
      multiDisplay: button?.multiDisplay === 'each' ? 'each' : 'total',
      min: low,
      max: high,
      value,
      rolls,
      resultLabel: resultRule?.label || null,
      resultIconSlug: resultRule?.iconSlug || null,
      resultColor: resultRule?.color || null,
      at: Date.now(),
      fromItemId,
      fromItemLabel,
    };

    const clonedDice = deepCloneArray(item?.dices || []);
    const idx = clonedDice.findIndex(obj => getId(obj) === diceId);
    if (idx === -1) return;

    const existing = clonedDice[idx]?.[diceId] || {};
    const safeLimit = historyLimit;
    const nextHistory =
      safeLimit > 0
        ? [
            entry,
            ...(Array.isArray(existing?.history) ? existing.history : []),
          ].slice(0, safeLimit)
        : [];

    clonedDice[idx] = {
      [diceId]: {
        ...existing,
        label: existing?.label ?? 'De',
        last: entry,
        history: nextHistory,
        historyLimit: safeLimit,
      },
    };

    update(`items/${itemId}`, 'dices', clonedDice);
  };

  const buttonsNode = (
    <div
      className={`bg-opacity-50 bg-main-d p-1 ${
        isWideLayout
          ? 'h-full shrink-0 overflow-y-auto overflow-x-hidden flex flex-col gap-05 items-stretch justify-start'
          : 'w-full overflow-x-auto overflow-y-hidden flex flex-wrap gap-05 items-center justify-center -mb-1 pb-2'
      }`}
    >
      {buttons.map((btn, idx) => (
        <button
          key={`${btn?.label || 'btn'}-${idx}`}
          type='button'
          className={`flex items-center justify-center gap-03 rounded shadow px-1 pt-02 cursor-pointer transition text-center uppercase font-condensed text-sm ${
            canRoll ? 'hover:opacity-50 bg-primary' : 'opacity-40 bg-primary'
          }`}
          onClick={e => {
            e.stopPropagation();
            rollNow(btn);
          }}
          disabled={!canRoll}
        >
          {btn?.label || 'd6'}
        </button>
      ))}
    </div>
  );

  return (
    <div
      className={`w-full h-full min-h-0 overflow-hidden flex items-stretch gap-05 ${
        isWideLayout ? 'flex-row' : 'flex-col'
      }`}
    >
      {isWideLayout && buttonsNode}

      {/* Keep padding for the content area, but let the buttons bar be full-bleed */}
      <div className='w-full min-w-0 flex-1 min-h-0 flex flex-col items-center p-1 gap-05'>
        <DiceSettings
          itemId={itemId}
          diceId={diceId}
          dice={dice}
          canEdit={canEdit}
        />

        <div className='flex-1 min-h-0 min-w-0 w-full overflow-hidden'>
          {showLastOnly ? (
            <DiceDisplayLast
              history={history}
              historyLimit={historyLimit}
              animByRollKey={animByRollKey}
              newestKey={newestKey}
              lastSeenRollKey={lastSeenRollKeyRef.current}
              mountedAt={mountedAtRef.current}
              animationMs={ROLL_ANIMATION_MS}
              buttons={buttons}
              items={items}
              contextItem={contextItem}
              item={item}
            />
          ) : historyLimit > 0 && history.length > 0 ? (
            <DiceDisplayHistory
              history={history}
              historyLimit={historyLimit}
              historyScrollRef={historyScrollRef}
              animByRollKey={animByRollKey}
              newestKey={newestKey}
              lastSeenRollKey={lastSeenRollKeyRef.current}
              mountedAt={mountedAtRef.current}
              animationMs={ROLL_ANIMATION_MS}
              buttons={buttons}
              items={items}
              contextItem={contextItem}
              item={item}
            />
          ) : (
            <div className='h-full w-full flex items-center justify-center text-xs text-contrast text-opacity-40'>
              aucun lancer
            </div>
          )}
        </div>
      </div>

      {!isWideLayout && buttonsNode}
    </div>
  );
};
